import React from 'react';
import {
  MobileHeader, Card, Button, IconButton, Badge, FilterChip,
  EmptyState, SkeletonCard, Alert,
} from '@parsina/framework';
import {
  UsersRound, SearchX, Plus, Stethoscope, MessagesSquare,
} from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { PracticeBanner, PracticeBadge } from '../../components/common/PracticeNotice.jsx';
import ErrorState from '../../components/common/ErrorState.jsx';
import { ShowMore } from '../../components/mobile/MobileKit.jsx';
import { ageLabel, patientName } from '../../lib/caseForm.js';
import { caseStatusKey, caseStatusVariant } from '../../constants/vocabulary.js';
import usePatientsScreen from './usePatientsScreen.js';
import PatientRowActions from './PatientRowActions.jsx';
import { CaseFormDialog, DeletePatientDialog } from './PatientDialogs.jsx';
import { STATUS_CHIPS, showingAll, isArchived } from './Patients.shared.js';

// «بیماران» on a phone — the same list, as cards. core.md §7.3.
//
// The table's six columns become one card each, in the order a thumb reads
// them: who, then the complaint, then how much work is already in there. The
// sort control is gone — the default («آخرین ویزیت») is the one a phone user
// wants, and a fourth control in a 360px toolbar costs more than the choice is
// worth. Everything else — the search, the chips, the actions, the two dialogs
// — is the same behaviour, from the same hook.

const SCROLL = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '12px 14px calc(88px + env(safe-area-inset-bottom, 0px))',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const FAB = {
  position: 'fixed',
  insetInlineEnd: 16,
  bottom: 'calc(var(--app-bottom-nav, 64px) + 16px + env(safe-area-inset-bottom, 0px))',
  zIndex: 20,
};

function PatientCard({ row, onOpen, onVisit, onChat, onArchive, onDelete }) {
  const { t, formatNumber, formatDate } = useTranslation();
  return (
    <Card
      onClick={() => onOpen(row)}
      role="button"
      data-testid={`patient-card-${row.id}`}
      style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 800, fontSize: 14, color: C.textPrimary }}>
          {patientName(row, t)}
        </span>
        <PracticeBadge short />
        <Badge variant={caseStatusVariant(row.status)} style={{ marginInlineStart: 'auto' }}>
          {t(caseStatusKey(row.status))}
        </Badge>
      </div>

      <div style={{ fontSize: 12, color: CT.mute }}>
        {[ageLabel(row, t), row.sex ? t(`common.gender.${row.sex}`) : '', row.caseRef]
          .filter(Boolean)
          .join(' · ')}
      </div>

      {row.chiefComplaint && (
        <div style={{ fontSize: 13, color: C.textPrimary }}>{row.chiefComplaint}</div>
      )}

      <div style={{ fontSize: 11.5, color: CT.mute }}>
        {/* The server's counts, never `.length` of a list the query left empty
            by contract. */}
        {t('patients.visitsCount', { count: formatNumber(Number(row.encounterCount || 0)) })}
        {row.lastEncounterAtMs ? ` · ${formatDate(row.lastEncounterAtMs, 'numeric')}` : ''}
      </div>

      <div
        style={{ display: 'flex', gap: 4, marginTop: 2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          variant="ghost"
          size={40}
          icon={<Stethoscope size={17} aria-hidden />}
          label={isArchived(row) ? t('record.header.archivedHint') : t('patients.actions.startVisit')}
          disabled={isArchived(row)}
          onClick={() => onVisit(row)}
          data-testid={`patient-visit-${row.id}`}
        />
        <IconButton
          variant="ghost"
          size={40}
          icon={<MessagesSquare size={17} aria-hidden />}
          label={t('patients.actions.chat')}
          onClick={() => onChat(row)}
          data-testid={`patient-chat-${row.id}`}
        />
        <span style={{ marginInlineStart: 'auto' }}>
          <PatientRowActions
            row={row}
            onOpen={onOpen}
            onArchive={onArchive}
            onDelete={onDelete}
            size={40}
          />
        </span>
      </div>
    </Card>
  );
}

export default function PatientsMobile() {
  const { t } = useTranslation();
  const { me } = useMe();
  const screen = usePatientsScreen();

  return (
    <>
      {/* Cockpit mode: `/patients` is a bottom-nav destination, so the header
          greets rather than offering a way back. The search rides ON the
          gradient (the kit's `search` prop) instead of costing a row below it. */}
      <MobileHeader
        user={{ name: me?.fullName || '' }}
        greeting={t('mobile.greeting')}
        title={t('patients.title')}
        sticky
        search={{
          value: screen.query,
          onChange: (value) => screen.setQuery(value),
          placeholder: t('patients.search'),
          onClear: () => screen.setQuery(''),
          clearLabel: t('common.actions.clear'),
          testId: 'patients-search',
        }}
        data-testid="patients-mobile-header"
      >
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
          {STATUS_CHIPS.map((chip) => (
            <FilterChip
              key={chip.id}
              label={t(chip.labelKey)}
              active={(screen.statuses || []).includes(chip.id)}
              onClick={() => screen.toggleStatusChip(chip.id)}
              data-testid={`patients-filter-${chip.id}`}
            />
          ))}
          <FilterChip
            label={t('patients.filters.all')}
            active={showingAll(screen.statuses)}
            onClick={screen.showAll}
            data-testid="patients-filter-all"
          />
        </div>
      </MobileHeader>

      <div style={SCROLL} data-testid="patients-mobile">
        <PracticeBanner />

        {screen.capped && !screen.loading && (
          <Alert variant="info" data-testid="patients-capped">{t('patients.capped')}</Alert>
        )}

        {screen.loading && (
          <div data-testid="patients-loading" style={{ display: 'grid', gap: 10 }}>
            {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {!screen.loading && screen.error && (
          <ErrorState
            error={screen.error}
            title={t('patients.error.title')}
            description={t('patients.error.body')}
            onRetry={screen.reload}
            data-testid="patients-error"
          />
        )}

        {/* «هنوز بیماری نساخته‌اید» ONLY when there is nothing in ANY status —
            the chips are a wire filter, and «بایگانی» with no archived rows is
            the filter's emptiness, not the student's (core.md §7.3). */}
        {screen.trulyEmpty && (
          <EmptyState
            icon={<UsersRound size={44} strokeWidth={1.5} aria-hidden />}
            title={t('patients.empty.title')}
            description={t('patients.empty.body')}
            action={
              <Button onClick={screen.openCreate} data-testid="patients-empty-new">
                {t('patients.new')}
              </Button>
            }
            data-testid="patients-empty"
          />
        )}

        {screen.filteredEmpty && (
          <EmptyState
            icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
            title={t('patients.noMatch.title')}
            description={t('patients.noMatch.body')}
            action={
              <Button variant="outline" onClick={screen.clearFilters} data-testid="patients-clear-filter">
                {t('patients.actions.clearFilters')}
              </Button>
            }
            data-testid="patients-no-match"
          />
        )}

        {/* The phone GROWS the list 25 at a time («نمایش بیشتر») instead of
            paging it: a tapped-into card has to be there when the student comes
            back, and a pager row is under the 44px touch floor. */}
        {!screen.loading && !screen.error && screen.shownRows.map((row) => (
          <PatientCard
            key={row.id}
            row={row}
            onOpen={screen.open}
            onVisit={screen.startVisit}
            onChat={screen.chat}
            onArchive={screen.archive}
            onDelete={screen.setDeleteTarget}
          />
        ))}
        {!screen.loading && !screen.error && screen.canShowMore && (
          <ShowMore onClick={screen.showMore} testId="patients-show-more" />
        )}
      </div>

      {/* The FAB clears the bottom bar through `--app-bottom-nav`, the variable
          the mobile viewport contract sets for exactly this. */}
      <Button
        onClick={screen.openCreate}
        style={FAB}
        className="min-h-11 rounded-full shadow-lg"
        data-testid="patients-new"
      >
        <Plus size={16} aria-hidden />
        {t('patients.new')}
      </Button>

      <CaseFormDialog
        open={screen.creating}
        onClose={screen.closeCreate}
        values={screen.form}
        onChange={screen.setForm}
        onSubmit={screen.submitCreate}
        saving={screen.saving}
        showTitleError={screen.showTitleError}
        mode="create"
      />
      <DeletePatientDialog
        row={screen.deleteTarget}
        onCancel={() => screen.setDeleteTarget(null)}
        onConfirm={screen.confirmDelete}
        loading={screen.deleting}
      />
    </>
  );
}
